<template>
  <div class="lease-container">
    <div class="container">
      <!-- 筛选区域 -->
      <div class="filter-card">
        <el-form :inline="true" :model="searchForm" class="filter-form">
          <el-form-item label="申请状态">
            <el-select
              v-model="searchForm.search_status"
              placeholder="请选择申请状态"
              clearable
              style="width: 200px"
              @change="handleStatusChange"
            >
              <el-option label="全部" value="" />
              <el-option label="待审批" value="pending_approval" />
              <el-option label="待签收" value="pending_signing" />
              <el-option label="租赁中" value="under_lease" />
              <el-option label="租赁历史" value="lease_history" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button @click="handleReset">重置</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 表格区域 -->
      <div class="table-card">
        <el-table
          :data="applicationList"
          stripe
          style="width: 100%"
          v-loading="loading"
          empty-text="暂无申请数据"
        >
          <el-table-column label="申请单号" width="180">
            <template #default="scope">
              <span 
                class="application-no-link" 
                @click="copyApplicationNo(scope.row.application_no)"
                :title="'点击复制：' + scope.row.application_no"
              >
                {{ scope.row.application_no }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="产品信息" min-width="200">
            <template #default="scope">
              <div class="product-info">
                <div class="product-name">
                  {{ scope.row.machine_model_json?.brand_name }} 
                  {{ scope.row.machine_model_json?.model_name }}
                </div>
                <div class="product-category">
                  {{ scope.row.machine_model_json?.category }} - 
                  {{ scope.row.machine_model_json?.sub_category }}
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="租赁模式" width="150">
            <template #default="scope">
              {{ scope.row.apply_rental_mode_json?.mode_name }}
            </template>
          </el-table-column>
          <el-table-column label="申请状态" width="120">
            <template #default="scope">
              <el-tag :type="getStatusTagType(scope.row.search_status)">
                {{ getStatusText(scope.row.search_status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="应付金额" width="120" align="right">
            <template #default="scope">
              <span class="amount-text">¥{{ scope.row.payable_total_amount }}</span>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" width="180">
            <template #default="scope">
              {{ formatDateTime(scope.row.create_datetime) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" fixed="right" width="150">
            <template #default="scope">
              <el-button
                v-if="scope.row.application_status === 'INIT_APPROVED' || scope.row.application_status === 'CONFIRMED'"
                link
                type="primary"
                size="small"
                @click="handlePay(scope.row)"
              >
                立即支付
              </el-button>
              <el-button
                link
                type="primary"
                size="small"
                @click="handleViewDetail(scope.row)"
              >
                查看详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            v-model:current-page="pagination.page"
            v-model:page-size="pagination.limit"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
          />
        </div>
      </div>

      <!-- 详情弹窗 -->
      <el-dialog
        v-model="detailDialogVisible"
        title="申请详情"
        width="900px"
        :close-on-click-modal="true"
      >
        <div v-if="currentApplication" class="detail-content">
          <!-- 基本信息 -->
          <div class="detail-section">
            <h3>基本信息</h3>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="申请编号">
                {{ currentApplication.application_no }}
              </el-descriptions-item>
              <el-descriptions-item label="申请状态">
                <el-tag :type="getStatusTagType(currentApplication.search_status)">
                  {{ getStatusText(currentApplication.search_status) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="联系人">
                {{ currentApplication.apply_contact_name }}
              </el-descriptions-item>
              <el-descriptions-item label="联系电话">
                {{ currentApplication.apply_user_phone }}
              </el-descriptions-item>
              <el-descriptions-item label="收货地址" :span="2">
                {{ currentApplication.user_receive_address }}
              </el-descriptions-item>
              <el-descriptions-item label="法人姓名">
                {{ currentApplication.legal_id_name }}
              </el-descriptions-item>
              <el-descriptions-item label="法人身份证号">
                {{ currentApplication.legal_id_number }}
              </el-descriptions-item>
              <el-descriptions-item label="创建时间">
                {{ formatDateTime(currentApplication.create_datetime) }}
              </el-descriptions-item>
              <el-descriptions-item label="更新时间">
                {{ formatDateTime(currentApplication.update_datetime) }}
              </el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 产品信息 -->
          <div class="detail-section">
            <h3>产品信息</h3>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="品牌">
                {{ currentApplication.machine_model_json?.brand_name }}
              </el-descriptions-item>
              <el-descriptions-item label="型号">
                {{ currentApplication.machine_model_json?.model_name }}
              </el-descriptions-item>
              <el-descriptions-item label="分类">
                {{ currentApplication.machine_model_json?.category }}
              </el-descriptions-item>
              <el-descriptions-item label="子分类">
                {{ currentApplication.machine_model_json?.sub_category }}
              </el-descriptions-item>
            </el-descriptions>
            <div v-if="currentApplication.machine_model_json?.model_url" class="product-image">
              <img :src="currentApplication.machine_model_json.model_url" alt="产品图片" />
            </div>
          </div>

          <!-- 租赁信息 -->
          <div class="detail-section">
            <h3>租赁信息</h3>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="租赁模式">
                {{ currentApplication.apply_rental_mode_json?.mode_name }}
              </el-descriptions-item>
              <el-descriptions-item label="预估时间数">
                {{ currentApplication.estimate_time_num }}
                {{ currentApplication.apply_rental_mode_json?.mode_code === 'MODE_C' ? '天' : '月' }}
              </el-descriptions-item>
              <el-descriptions-item label="是否免押金">
                <el-tag :type="currentApplication.is_deposit_free ? 'success' : 'info'">
                  {{ currentApplication.is_deposit_free ? '是' : '否' }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="用途说明">
                {{ currentApplication.rental_purpose || '-' }}
              </el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 费用信息 -->
          <div class="detail-section">
            <h3>费用信息</h3>
            <el-descriptions :column="2" border>
              <el-descriptions-item label="预估总金额">
                ¥{{ currentApplication.estimated_total_amount }}
              </el-descriptions-item>
              <el-descriptions-item label="优惠金额">
                ¥{{ currentApplication.discount_amount || 0 }}
              </el-descriptions-item>
              <el-descriptions-item label="应付总金额">
                <span class="amount-text">¥{{ currentApplication.payable_total_amount }}</span>
              </el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 身份证信息 -->
          <div class="detail-section">
            <h3>身份证信息</h3>
            <div class="id-card-images">
              <div v-if="currentApplication.legal_id_front_img" class="id-card-item">
                <div class="id-card-label">身份证正面</div>
                <img :src="currentApplication.legal_id_front_img" alt="身份证正面" @click="previewImage(currentApplication.legal_id_front_img)" />
              </div>
              <div v-if="currentApplication.legal_id_back_img" class="id-card-item">
                <div class="id-card-label">身份证反面</div>
                <img :src="currentApplication.legal_id_back_img" alt="身份证反面" @click="previewImage(currentApplication.legal_id_back_img)" />
              </div>
            </div>
          </div>
        </div>
      </el-dialog>

      <!-- 确认订单弹窗 -->
      <el-dialog
        v-model="confirmDialogVisible"
        title="确认订单信息"
        width="600px"
        :close-on-click-modal="false"
      >
        <div class="confirm-content">
          <p class="confirm-tip">请仔细核对以下订单信息，确认无误后点击确认按钮：</p>
          <el-descriptions :column="1" border>
            <el-descriptions-item label="申请编号">
              {{ currentPayApplication?.application_no }}
            </el-descriptions-item>
            <el-descriptions-item label="产品信息">
              {{ currentPayApplication?.machine_model_json?.brand_name }} 
              {{ currentPayApplication?.machine_model_json?.model_name }}
            </el-descriptions-item>
            <el-descriptions-item label="租赁模式">
              {{ currentPayApplication?.apply_rental_mode_json?.mode_name }}
            </el-descriptions-item>
            <el-descriptions-item label="应付金额">
              <span class="amount-text">¥{{ currentPayApplication?.payable_total_amount }}</span>
            </el-descriptions-item>
          </el-descriptions>
        </div>
        <template #footer>
          <el-button @click="confirmDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmOrder" :loading="isConfirming">
            确认订单信息
          </el-button>
        </template>
      </el-dialog>

      <!-- 支付二维码弹窗 -->
      <el-dialog
        v-model="paymentDialogVisible"
        title="请使用微信扫码支付"
        width="400px"
        :close-on-click-modal="true"
        :close-on-press-escape="true"
        @close="handlePaymentCancel"
      >
        <div class="payment-qrcode-container">
          <div v-if="qrcodeLoading" class="qrcode-loading">
            <el-icon class="is-loading"><Loading /></el-icon>
            <p>正在生成支付二维码...</p>
          </div>
          <div v-else-if="qrcodeError" class="qrcode-error">
            <el-icon><WarningFilled /></el-icon>
            <p>{{ qrcodeError }}</p>
            <el-button type="primary" @click="loadQRCode">重试</el-button>
          </div>
          <div v-else class="qrcode-wrapper">
            <img :src="qrcodeImageUrl" alt="支付二维码" class="qrcode-image" />
            <p class="qrcode-order">订单号：{{ currentOrderId }}</p>
          </div>
        </div>
      </el-dialog>

      <!-- 图片预览 -->
      <Preview
        v-if="previewImageUrl"
        :data="[previewImageUrl]"
        :index="0"
        type="image"
        @close="previewImageUrl = ''"
      />
    </div>
  </div>
</template>

<style scoped>
.lease-container {
  min-height: 100vh;
  background: #f5f6fa;
  padding-top: 1rem;
  padding-bottom: 3rem;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-header {
  text-align: center;
  margin-bottom: 30px;
}

.page-header h1 {
  font-size: 2.5em;
  color: #333;
  margin-bottom: 10px;
  font-weight: bold;
}

.page-header p {
  font-size: 1.1em;
  color: #666;
}

.filter-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.filter-form {
  margin: 0;
}

.table-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.product-name {
  font-weight: 600;
  color: #333;
}

.product-category {
  font-size: 12px;
  color: #999;
}

.amount-text {
  font-weight: bold;
  color: #f56c6c;
  font-size: 1.1em;
}

.application-no-link {
  color: #409eff;
  cursor: pointer;
  text-decoration: none;
  transition: color 0.3s;
}

.application-no-link:hover {
  color: #66b1ff;
  text-decoration: underline;
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.detail-content {
  max-height: 70vh;
  overflow-y: auto;
}

.detail-section {
  margin-bottom: 30px;
}

.detail-section h3 {
  font-size: 18px;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e5e5e5;
}

.product-image {
  margin-top: 15px;
  text-align: center;
}

.product-image img {
  max-width: 300px;
  max-height: 200px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
}

.fee-item {
  margin-top: 15px;
}

.fee-item h4 {
  font-size: 16px;
  color: #666;
  margin-bottom: 10px;
}

.id-card-images {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.id-card-item {
  flex: 1;
  min-width: 300px;
  max-width: 400px;
}

.id-card-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
}

.id-card-item img {
  width: 100%;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  cursor: pointer;
  transition: transform 0.3s;
}

.id-card-item img:hover {
  transform: scale(1.05);
}

@media (max-width: 768px) {
  .lease-container {
    padding-top: 8rem;
  }

  .page-header h1 {
    font-size: 2em;
  }

  .id-card-images {
    flex-direction: column;
  }

  .id-card-item {
    max-width: 100%;
  }
}

/* 确认订单弹窗样式 */
.confirm-content {
  padding: 20px 0;
}

.confirm-tip {
  margin-bottom: 20px;
  color: #666;
  font-size: 14px;
}

/* 支付二维码弹窗样式 */
.payment-qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 300px;
}

.qrcode-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  color: #666;
}

.qrcode-loading .el-icon {
  font-size: 40px;
  color: #667eea;
}

.qrcode-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  color: #f56c6c;
}

.qrcode-error .el-icon {
  font-size: 40px;
}

.qrcode-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.qrcode-image {
  width: 250px;
  height: 250px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px;
  background: white;
}

.qrcode-tip {
  font-size: 16px;
  color: #333;
  font-weight: 500;
  margin: 0;
}

.qrcode-order {
  font-size: 12px;
  color: #999;
  margin: 0;
}
</style>

<script setup>
import { ref, reactive, onMounted, onUnmounted } from 'vue'
import apiClient from '@/components/plugins/axios'
import { ElMessage } from 'element-plus'
import { Loading, WarningFilled } from '@element-plus/icons-vue'
import Preview from '@/components/templates/Preview.vue'

// 加载状态
const loading = ref(false)

// 申请列表
const applicationList = ref([])

// 搜索表单
const searchForm = reactive({
  search_status: ''
})

// 分页信息
const pagination = reactive({
  page: 1,
  limit: 10,
  total: 0
})

// 详情弹窗
const detailDialogVisible = ref(false)
const currentApplication = ref(null)
const previewImageUrl = ref('')

// 确认订单弹窗
const confirmDialogVisible = ref(false)
const currentPayApplication = ref(null)
const isConfirming = ref(false)

// 支付二维码相关
const paymentDialogVisible = ref(false)
const qrcodeImageUrl = ref('')
const qrcodeLoading = ref(false)
const qrcodeError = ref('')
const currentOrderId = ref('')
const paymentCheckTimer = ref(null)

// 获取申请列表
async function loadApplicationList() {
  loading.value = true
  try {
    const params = {
      page: pagination.page,
      limit: pagination.limit
    }

    if (searchForm.search_status) {
      params.search_status = searchForm.search_status
    }

    const res = await apiClient.get('/api/v1/rental/client/applications', { params })

    if (res.data && res.data.code === 200) {
      applicationList.value = res.data.data || []
      pagination.total = res.data.count || 0
    } else {
      ElMessage.error(res.data?.message || '获取申请列表失败')
    }
  } catch (error) {
    console.error('获取申请列表失败:', error)
    ElMessage.error('获取申请列表失败，请稍后重试')
  } finally {
    loading.value = false
  }
}

// 获取状态标签类型
function getStatusTagType(status) {
  const statusMap = {
    'pending_approval': 'info',
    'pending_signing': 'warning',
    'under_lease': 'success',
    'lease_history': 'success'
  }
  return statusMap[status] || ''
}

// 获取状态文本
function getStatusText(status) {
  const statusMap = {
    'pending_approval': '待审批',
    'pending_signing': '待签收',
    'under_lease': '租赁中',
    'lease_history': '租赁历史'
  }
  return statusMap[status] || status
}

// 格式化日期时间
function formatDateTime(dateTime) {
  if (!dateTime) return '-'
  const date = new Date(dateTime)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 状态筛选改变
function handleStatusChange() {
  pagination.page = 1
  loadApplicationList()
}

// 重置筛选
function handleReset() {
  searchForm.search_status = ''
  pagination.page = 1
  loadApplicationList()
}

// 分页大小改变
function handleSizeChange(val) {
  pagination.limit = val
  pagination.page = 1
  loadApplicationList()
}

// 页码改变
function handlePageChange(val) {
  pagination.page = val
  loadApplicationList()
}

// 立即支付
function handlePay(row) {
  currentPayApplication.value = row
  
  // 如果是 INIT_APPROVED 状态，需要先确认订单
  if (row.application_status === 'INIT_APPROVED') {
    confirmDialogVisible.value = true
  } else if (row.application_status === 'CONFIRMED') {
    // CONFIRMED 状态直接调用支付接口
    createPaymentOrder(row.application_no)
  }
}

// 确认订单
async function confirmOrder() {
  if (!currentPayApplication.value) {
    return
  }

  isConfirming.value = true
  try {
    const res = await apiClient.post(`/api/v1/rental/confirm/${currentPayApplication.value.application_no}`, {
      is_agree: true
    })

    if (res.data && res.data.code === 200) {
      ElMessage.success('订单确认成功')
      confirmDialogVisible.value = false
      // 确认成功后调用支付接口
      await createPaymentOrder(currentPayApplication.value.application_no)
    } else {
      ElMessage.error(res.data?.message || '订单确认失败')
    }
  } catch (error) {
    console.error('确认订单失败:', error)
    ElMessage.error(error.response?.data?.message || '订单确认失败，请稍后重试')
  } finally {
    isConfirming.value = false
  }
}

// 创建支付订单
async function createPaymentOrder(applicationNo) {
  try {
    ElMessage.info('正在创建支付订单...')
    
    const res = await apiClient.post(`/api/v1/rental/pay_first/${applicationNo}`, {
      payment_channel:"wechat"
    })


    if (res.data && res.data.code === 200) {
      const orderId = res.data.data?.order_id
      if (!orderId) {
        throw new Error('订单创建失败，未返回订单ID')
      }
      
      currentOrderId.value = orderId
      // 加载支付二维码
      await loadQRCode()
    } else {
      throw new Error(res.data?.message || '创建支付订单失败')
    }
  } catch (error) {
    console.error('创建支付订单失败:', error)
    ElMessage.error(error.response?.data?.message || error.message || '创建支付订单失败，请稍后重试')
  }
}

// 加载支付二维码
async function loadQRCode() {
  if (!currentOrderId.value) {
    ElMessage.error('订单ID不存在')
    return
  }

  qrcodeLoading.value = true
  qrcodeError.value = ''

  try {
    const response = await apiClient.get('/api/v1/payment/qrcode/image', {
      params: {
        order_number: currentOrderId.value
      },
      responseType: 'blob'
    })

    // 清理旧的二维码URL
    if (qrcodeImageUrl.value) {
      URL.revokeObjectURL(qrcodeImageUrl.value)
    }

    // 将响应转换为blob，然后创建URL
    const blob = response.data
    qrcodeImageUrl.value = URL.createObjectURL(blob)

    // 显示支付弹窗
    paymentDialogVisible.value = true

    // 开始轮询检查支付状态
    startPaymentCheck()
  } catch (error) {
    console.error('加载二维码失败:', error)
    qrcodeError.value = error.response?.data?.message || error.message || '获取支付二维码失败，请稍后重试'
    qrcodeImageUrl.value = ''
  } finally {
    qrcodeLoading.value = false
  }
}

// 开始轮询检查支付状态
function startPaymentCheck() {
  // 清除之前的定时器
  if (paymentCheckTimer.value) {
    clearInterval(paymentCheckTimer.value)
  }

  // 每3秒检查一次支付状态
  paymentCheckTimer.value = setInterval(() => {
    checkPaymentStatus(true) // true表示静默检查
  }, 3000)
}

// 检查支付状态
async function checkPaymentStatus(silent = false) {
  if (!currentOrderId.value) {
    return
  }

  try {
    const response = await apiClient.get(`/api/v1/payment/detail_by_no/${currentOrderId.value}`)

    if (response.data && response.data.code === 200) {
      const orderData = response.data.data

      // 检查订单状态
      if (orderData.order_status === 'paid') {
        // 支付成功，处理支付成功逻辑
        handlePaymentSuccess()
        return
      }
    }
  } catch (error) {
    console.error('检查支付状态失败:', error)
    if (!silent) {
      ElMessage.error(error.response?.data?.message || error.message || '检查支付状态失败，请稍后重试')
    }
  }
}

// 支付成功处理
async function handlePaymentSuccess() {
  // 清除定时器
  if (paymentCheckTimer.value) {
    clearInterval(paymentCheckTimer.value)
    paymentCheckTimer.value = null
  }

  // 关闭支付弹窗
  paymentDialogVisible.value = false

  // 清理二维码图片URL
  if (qrcodeImageUrl.value) {
    URL.revokeObjectURL(qrcodeImageUrl.value)
    qrcodeImageUrl.value = ''
  }

  ElMessage.success('支付成功！')

  // 刷新申请列表
  await loadApplicationList()
}

// 取消支付
function handlePaymentCancel() {
  // 清除定时器
  if (paymentCheckTimer.value) {
    clearInterval(paymentCheckTimer.value)
    paymentCheckTimer.value = null
  }

  // 关闭支付弹窗
  paymentDialogVisible.value = false

  // 清理二维码图片URL
  if (qrcodeImageUrl.value) {
    URL.revokeObjectURL(qrcodeImageUrl.value)
    qrcodeImageUrl.value = ''
  }

  // 清空订单ID
  currentOrderId.value = ''
  qrcodeError.value = ''
}

// 查看详情
function handleViewDetail(row) {
  currentApplication.value = row
  detailDialogVisible.value = true
}

// 预览图片
function previewImage(url) {
  previewImageUrl.value = url
}

// 复制申请单号
async function copyApplicationNo(applicationNo) {
  try {
    await navigator.clipboard.writeText(applicationNo)
    ElMessage.success('申请单号已复制到剪贴板')
  } catch (error) {
    // 如果 Clipboard API 不可用，使用备用方法
    const textArea = document.createElement('textarea')
    textArea.value = applicationNo
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    document.body.appendChild(textArea)
    textArea.select()
    try {
      document.execCommand('copy')
      ElMessage.success('申请单号已复制到剪贴板')
    } catch (err) {
      ElMessage.error('复制失败，请手动复制')
    }
    document.body.removeChild(textArea)
  }
}

onMounted(() => {
  loadApplicationList()
})

onUnmounted(() => {
  // 清理支付检查定时器
  if (paymentCheckTimer.value) {
    clearInterval(paymentCheckTimer.value)
    paymentCheckTimer.value = null
  }

  // 清理二维码图片URL
  if (qrcodeImageUrl.value) {
    URL.revokeObjectURL(qrcodeImageUrl.value)
    qrcodeImageUrl.value = ''
  }
})
</script>